import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  IPurchaseOrder,
  IPurchaseOrderPage,
  IPurchaseOrderRequest,
  IPurchaseOrderApprovalRequest,
} from '../type/purchaseOrder';
import apiClient from '../service/apiClient';
import { toast } from 'react-toastify';

const EXCEL_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const buildPurchaseOrderParams = (request: IPurchaseOrderRequest) => {
  const params: Record<string, any> = { ...request };

  if (params.startDt) {
    params.startDt = params.startDt + 'T00:00:00';
  } else if (params.startDt === '') {
    delete params.startDt;
  }

  if (params.endDt) {
    params.endDt = params.endDt + 'T23:59:59';
  } else if (params.endDt === '') {
    delete params.endDt;
  }

  if (params.purchaseType === '') {
    delete params.purchaseType;
  }

  if (params.franchiseId === '') {
    delete params.franchiseId;
  }

  if (params.page === undefined || params.page === null) {
    delete params.page;
  }

  if (params.size === undefined || params.size === null) {
    delete params.size;
  }

  return params;
};

type PurchaseOrderStoreData = {
  purchaseOrders: IPurchaseOrder[];
  purchaseOrderPage: IPurchaseOrderPage | null;
  getPurchaseOrdersLoading: boolean;
  approvePurchaseOrdersLoading: boolean;
  downloadPurchaseOrdersLoading: boolean;
  approvedAmountSum: number | null;
  getApprovedAmountSumLoading: boolean;
};

type PurchaseOrderStoreAction = {
  getPurchaseOrders: (request: IPurchaseOrderRequest) => Promise<void>;
  downloadPurchaseOrdersExcel: (request: IPurchaseOrderRequest) => Promise<void>;
  approvePurchaseOrders: (
    request: IPurchaseOrderApprovalRequest,
    memo?: string
  ) => Promise<boolean>;
  rejectPurchaseOrders: (
    request: IPurchaseOrderApprovalRequest,
    memo?: string
  ) => Promise<boolean>;
  clearPurchaseOrders: () => void;
  getApprovedAmountSum: (startDate: string, endDate: string) => Promise<void>;
};

export const usePurchaseOrderStore = create(
  persist<PurchaseOrderStoreData & PurchaseOrderStoreAction>(
    (set, get) => ({
      purchaseOrders: [],
      purchaseOrderPage: null,
      getPurchaseOrdersLoading: false,
      approvePurchaseOrdersLoading: false,
      downloadPurchaseOrdersLoading: false,
      approvedAmountSum: null,
      getApprovedAmountSumLoading: false,

      getPurchaseOrders: async (request: IPurchaseOrderRequest) => {
        set({ getPurchaseOrdersLoading: true });
        try {
          const params = buildPurchaseOrderParams(request);

          const response = await apiClient.get('/purchase-order', {
            params,
          });

          set({
            purchaseOrders: response.data.content,
            purchaseOrderPage: response.data,
            getPurchaseOrdersLoading: false,
          });
        } catch (error: any) {
          console.error('입금대기 목록 조회 실패:', error);
          toast.error(
            error.response?.data?.message || '입금대기 목록 조회에 실패했습니다.'
          );
          set({ getPurchaseOrdersLoading: false });
        }
      },

      downloadPurchaseOrdersExcel: async (request: IPurchaseOrderRequest) => {
        set({ downloadPurchaseOrdersLoading: true });
        try {
          const params = buildPurchaseOrderParams(request);
          const response = await apiClient.get('/purchase-order/download', {
            params,
            responseType: 'blob',
          });

          const blob = new Blob([response.data], { type: EXCEL_MIME_TYPE });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          const now = new Date();
          const yyyy = now.getFullYear();
          const mm = (now.getMonth() + 1).toString().padStart(2, '0');
          const dd = now.getDate().toString().padStart(2, '0');
          link.href = url;
          link.setAttribute(
            'download',
            `purchase-order-${yyyy}${mm}${dd}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error: any) {
          console.error('입금대기 엑셀 다운로드 실패:', error);
          toast.error(
            error.response?.data?.message || '입금대기 엑셀 다운로드에 실패했습니다.'
          );
        }
        set({ downloadPurchaseOrdersLoading: false });
      },

      approvePurchaseOrders: async (
        request: IPurchaseOrderApprovalRequest,
        memo?: string
      ) => {
        set({ approvePurchaseOrdersLoading: true });
        try {
          const params = new URLSearchParams();
          request.purchaseOrderIds.forEach(id => params.append('purchaseOrderIds', id));
          if (memo) {
            params.append('memo', memo);
          }

          await apiClient.post(`/purchase-order/agree/charge?${params.toString()}`);
          toast.success('선택한 입금대기 건이 승인되었습니다.');
          set({ approvePurchaseOrdersLoading: false });
          return true;
        } catch (error: any) {
          console.error('입금대기 승인 실패:', error);
          toast.error(
            error.response?.data?.message || '입금대기 승인에 실패했습니다.'
          );
          set({ approvePurchaseOrdersLoading: false });
          return false;
        }
      },

      rejectPurchaseOrders: async (
        request: IPurchaseOrderApprovalRequest,
        memo?: string
      ) => {
        set({ approvePurchaseOrdersLoading: true });
        try {
          const params = new URLSearchParams();
          request.purchaseOrderIds.forEach(id => params.append('purchaseOrderIds', id));
          if (memo) {
            params.append('memo', memo);
          }

          await apiClient.post(`/purchase-order/agree/reject?${params.toString()}`);
          toast.success('선택한 입금대기 건이 거절되었습니다.');
          set({ approvePurchaseOrdersLoading: false });
          return true;
        } catch (error: any) {
          console.error('입금대기 거절 실패:', error);
          toast.error(
            error.response?.data?.message || '입금대기 거절에 실패했습니다.'
          );
          set({ approvePurchaseOrdersLoading: false });
          return false;
        }
      },

      clearPurchaseOrders: () => {
        set({
          purchaseOrders: [],
          purchaseOrderPage: null,
        });
      },

      getApprovedAmountSum: async (startDate: string, endDate: string) => {
        set({ getApprovedAmountSumLoading: true });
        try {
          const response = await apiClient.get('/purchase-order/statistics', {
            params: { startDate, endDate },
          });
          set({
            approvedAmountSum: response.data,
            getApprovedAmountSumLoading: false,
          });
        } catch (error: any) {
          console.error('승인 금액 합계 조회 실패:', error);
          toast.error(
            error.response?.data?.message || '승인 금액 합계 조회에 실패했습니다.'
          );
          set({ getApprovedAmountSumLoading: false });
        }
      },
    }),
    {
      name: 'purchaseOrderStore',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
